
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { Redirect, NewRedirect, StatusCode } from "@/types/redirectTypes";
import { createRedirect, updateRedirect, checkRedirectExists } from "@/services/redirectService";

interface UseRedirectFormProps {
  onSuccess?: (redirect: Redirect) => void;
  initialData?: Redirect;
}

export const useRedirectForm = ({ onSuccess, initialData }: UseRedirectFormProps = {}) => {
  const { toast } = useToast();
  const [sourcePath, setSourcePath] = useState(initialData?.source_path || "");
  const [targetPath, setTargetPath] = useState(initialData?.target_path || "");
  const [statusCode, setStatusCode] = useState<StatusCode>(initialData?.status_code || 301);
  const [isActive, setIsActive] = useState(initialData?.is_active ?? true);
  const [notes, setNotes] = useState(initialData?.notes || "");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{
    sourcePath?: string;
    targetPath?: string;
  }>({});

  const validateForm = async (): Promise<boolean> => {
    const errors: { sourcePath?: string; targetPath?: string } = {};
    
    if (!sourcePath.trim()) {
      errors.sourcePath = "Source path is required";
    } else if (!/^\/[a-zA-Z0-9\-\/]*$/.test(sourcePath)) {
      errors.sourcePath = "Source path must start with / and contain only letters, numbers, hyphens, and slashes";
    } else {
      // Check if a redirect with this source path already exists
      try {
        const exists = await checkRedirectExists(sourcePath, initialData?.id);
        if (exists) {
          errors.sourcePath = "A redirect with this source path already exists";
        }
      } catch (error) {
        toast({
          title: "Error",
          description: "Failed to check for duplicate redirects",
          variant: "destructive",
        });
        return false;
      }
    }
    
    if (!targetPath.trim()) {
      errors.targetPath = "Target path is required";
    } else if (!/^\/[a-zA-Z0-9\-\/]*$/.test(targetPath)) {
      errors.targetPath = "Target path must start with / and contain only letters, numbers, hyphens, and slashes";
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const isValid = await validateForm();
      if (!isValid) {
        setIsSubmitting(false);
        return;
      }
      
      const redirectData: NewRedirect = {
        source_path: sourcePath,
        target_path: targetPath,
        status_code: statusCode,
        is_active: isActive,
        notes
      };
      
      let result: Redirect;
      
      if (initialData?.id) {
        // Update existing redirect
        result = await updateRedirect(initialData.id, redirectData);
        toast({
          title: "Success",
          description: "Redirect updated successfully",
        });
      } else {
        // Create new redirect
        result = await createRedirect(redirectData);
        toast({
          title: "Success",
          description: "Redirect created successfully",
        });
      }
      
      if (onSuccess) {
        onSuccess(result);
      }
      
      // Reset form if creating a new redirect
      if (!initialData) {
        setSourcePath("");
        setTargetPath("");
        setStatusCode(301);
        setIsActive(true);
        setNotes("");
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to save redirect",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    sourcePath,
    setSourcePath,
    targetPath,
    setTargetPath,
    statusCode,
    setStatusCode,
    isActive,
    setIsActive,
    notes,
    setNotes,
    isSubmitting,
    validationErrors,
    handleSubmit
  };
};
